import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Input } from '../ui';
import { getGroupPlayers, setNumerosGrupo } from '../../lib/supabase';
import { detectarNumeros } from '../../lib/numerosGrupo';
import { cn, fixMojibake } from '../../lib/utils';

// Editor de numeros de camiseta de un grupo (por grupo = jugadores_grupos.numero_jersey).
// Prellena con el numero del grupo, o el principal del club como default. Valida en vivo:
// repetidos (bloqueante) y faltantes (aviso). Persiste con setNumerosGrupo.
// Props:
//  - grupoId
//  - onEstadoChange({ hayRepetidos, faltantes, total })  (opcional)
//  - saveRef  ref cuyo .current queda con una funcion async de guardado (opcional)
const NumerosGrupoEditor = ({ grupoId, onEstadoChange, saveRef }) => {
  const [filas, setFilas] = useState([]);
  const [loading, setLoading] = useState(true);
  const filasRef = useRef([]);

  useEffect(() => { filasRef.current = filas; }, [filas]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      const { data } = await getGroupPlayers(grupoId);
      if (cancel) return;
      const fs = (data || [])
        .filter((row) => row.activo !== false && row.jugadores)
        .map((row) => ({
          jugadorGrupoId: row.id,
          nombre: row.jugadores.nombre_completo,
          numero: row.numero_jersey != null
            ? String(row.numero_jersey)
            : (row.jugadores.numero != null ? String(row.jugadores.numero) : ''),
        }));
      setFilas(fs);
      setLoading(false);
    })();
    return () => { cancel = true; };
  }, [grupoId]);

  // Deja en saveRef.current una funcion de guardado que lee el estado actual (via ref).
  useEffect(() => {
    if (saveRef) {
      saveRef.current = async () => setNumerosGrupo(
        grupoId,
        filasRef.current.map((f) => ({ jugadorGrupoId: f.jugadorGrupoId, numero: f.numero }))
      );
    }
  }, [grupoId, saveRef]);

  const { repetidos, faltantes } = detectarNumeros(filas.map((f) => f.numero));

  useEffect(() => {
    onEstadoChange?.({ hayRepetidos: repetidos.size > 0, faltantes, total: filas.length });
  }, [filas]); // eslint-disable-line react-hooks/exhaustive-deps

  const setNumero = (jgId, val) => {
    setFilas((prev) => prev.map((f) => (f.jugadorGrupoId === jgId ? { ...f, numero: val } : f)));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando jugadores...
      </div>
    );
  }
  if (filas.length === 0) {
    return <p className="py-3 text-sm text-muted-foreground">Este grupo no tiene jugadores todavía.</p>;
  }

  return (
    <div className="space-y-2">
      <div className="divide-y rounded-lg border">
        {filas.map((f) => {
          const dup = f.numero.trim() !== '' && repetidos.has(f.numero.trim());
          return (
            <div key={f.jugadorGrupoId} className="flex items-center gap-3 px-3 py-2">
              <Input
                type="number"
                min="0"
                max="999"
                value={f.numero}
                onChange={(e) => setNumero(f.jugadorGrupoId, e.target.value)}
                className={cn('h-8 w-16 text-center tabular-nums', dup && 'border-destructive focus-visible:ring-destructive')}
                placeholder="#"
                aria-label={`Número de ${fixMojibake(f.nombre || '')}`}
              />
              <span className="min-w-0 flex-1 truncate text-sm">{fixMojibake(f.nombre || 'Jugador')}</span>
            </div>
          );
        })}
      </div>

      {repetidos.size > 0 && (
        <p className="flex items-center gap-1.5 text-xs text-destructive">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          Hay números repetidos ({Array.from(repetidos).join(', ')}). Corrígelos para continuar.
        </p>
      )}
      {faltantes > 0 && (
        <p className="flex items-center gap-1.5 text-xs text-amber-600">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {faltantes} jugador{faltantes === 1 ? '' : 'es'} sin número (puedes continuar y asignarlos después).
        </p>
      )}
    </div>
  );
};

export default NumerosGrupoEditor;
